<template>
  <div>
    <GettyPage
      :application-base-url="applicationBaseUrl"
      :class="{ 'v-base__footer--no-margin-top': currentTitle == 'Home' }"
      :alerts="alerts"
    >
      <Hero name="ResearchApplication" :data="heroData" :hide-links="true">
        <template v-if="useStickyBox" #content>
          <StickyBox v-bind="stickyBoxData" />
        </template>
      </Hero>
      <section>
        <div class="gui-container app-co">
          <slot v-if="hasDefaultSlotContent"></slot>
          <router-view v-else />
        </div>
      </section>
    </GettyPage>
  </div>
</template>

<script>
import {
  localizationMixin,
  slotContentMixin,
  useEventBus
} from "@thegetty-private/getty-ui";
import { AnalyticsMixin as analyticsMixin } from "@thegetty-private/rad-app-utils";
import app_nav from "@data/app_nav.json";
import * as configHelper from "@/helpers/configHelper";
import { GettyPage, Hero } from "@thegetty-private/getty-ui";
import StickyBox from "@/components/atoms/stickyBox/index.vue";

export default {
  name: "LayoutBase",
  components: { GettyPage, Hero, StickyBox },
  mixins: [localizationMixin, analyticsMixin, slotContentMixin],
  provide() {
    return {
      /**
       * updates the current page title
       * @param {string} title
       */
      updatePageTitle: (title) => {
        this.updateTitle(title);
      },
      /**
       * Turn off Markdown in DelimitedFieldValues
       */
      useMarkdownInDelimitedValues: false
    };
  },
  data() {
    return {
      /**
       * Alerts to display on the page
       */
      alerts: [],
      /**
       * The current URL path
       */
      currentPath: undefined,
      /**
       * The current page title
       */
      currentTitle: undefined,
      /**
       * Event bus composable instance
       */
      eventBus: useEventBus(),
      /**
       * The data object to populate the StickyBox component
       * @type {Object: Object.<string, string>}
       *
       * heading: the heading for the stickybox
       * subheading: the subheading for the stickybox
       * imageUuid: the uuid for the image to display
       * imageAlt: the alt text for the displayed image
       * isDeaccessioned: (optional) whether object is deaccessioned
       *
       */
      stickyBoxData: {},
      originalHeroData: {},
      heroData: {},
      /**
       * Page metadata to pass through to Analytics
       */
      pageMetadata: undefined
    };
  },
  computed: {
    /**
     * The base Url for the website you're on (e.g. getty.edu)
     * @returns {string}
     */
    applicationBaseUrl() {
      return configHelper.getGettyUIBaseUrl();
    },
    /**
     * Boolean indicating whether or not there is stickyBoxData,
     * thus determining if we should even pass anything to the slot
     *
     * @returns {boolean}
     */
    useStickyBox() {
      return Object.keys(this.stickyBoxData).length != 0;
    }
  },
  watch: {
    $route(to, from) {
      // if the route doesn't change, don't do anything
      if (to === from) {
        return;
      }
      this.currentTitle = undefined;
      this.updatePath();
      this.swapHeroInfo();

      // reset analytics metadata fields
      this.pageMetadata = undefined;

      // don't reset the stickybox if we're just updating params
      if (to.path != from.path) {
        this.resetStickyBox(to.name);
      }
    },
    /**
     * if the title changes and the title is not undefined, send a pageview
     *
     * @param {string} to - the new title
     * @param {string} from - the old title
     */
    currentTitle(to, from) {
      if (from != undefined || to == undefined) {
        return;
      }

      // check for additional metadata to pass to Plausible
      let extra = {};

      if (this.pageMetadata) {
        extra = { ...this.pageMetadata };
      }

      this.sendPageview(this.currentTitle, extra);
    }
  },
  created() {
    // listener for the stickybox data
    this.eventBus.on("data-ready", this.updateStickyBoxData);

    // listener for analytics page metadata
    this.eventBus.on("PageMetadata", (metadata) => {
      this.pageMetadata = metadata;
    });

    // listener for ImageViewerInteractionEvent analytics data
    this.eventBus.on("ImageViewerInteractionEvent", this.sendEvent);
    this.eventBus.on("AnalyticsEvent", this.sendEvent);

    let heroData = {
      title: this.translatePart("application_nav.applicationName"),
      backgroundColor: "magenta",
      showLinksHeading: false
    };
    // take navigation links data and translate applicable fields
    let navLinks = app_nav.map((link) => {
      link.text = this.translatePart(link.text);
      link.ariaLabel = this.translatePart(link.ariaLabel);
      link.active = false;
      if (link && link.to && link.to.name == this.$route.name) {
        link.active = true;
      }
      return link;
    });

    heroData.links = navLinks;

    const alert = configHelper.getAlert();
    if (alert) {
      this.alerts[0] = alert;
    }

    // add hero heading level based on path
    heroData.headingLevel = this.$route.path == "/" ? 1 : 2;
    this.heroData = heroData;
    this.originalHeroData = JSON.parse(JSON.stringify(heroData));
    this.swapHeroInfo();
  },
  mounted() {
    this.updatePath();
  },
  unmounted() {
    this.eventBus.off("data-ready", this.updateStickyBoxData);
    this.eventBus.off("ImageViewerInteractionEvent", this.sendEvent);
    this.eventBus.off("AnalyticsEvent", this.sendEvent);
  },
  methods: {
    /**
     * this swaps the hero info if we're on an exhibition page
     */
    swapHeroInfo() {
      const route = this.$route;
      let isExhibitionPage = false;
      if (["exhibition", "pastExhibitions"].includes(route.name)) {
        isExhibitionPage = true;
      }

      if (isExhibitionPage) {
        this.heroData.title = this.translatePart(
          "application_nav.altApplicationName"
        );
      } else {
        this.heroData = JSON.parse(JSON.stringify(this.originalHeroData));
      }
    },
    /**
     * Reset stickybox data on non-matching route name
     * @param {string} name - the name of the next route
     */
    resetStickyBox(_name) {
      // leaving in, in case we do need this
      // let validRoutes = ["object", "person", "group", "exhibition", "gallery"];
      // if (!validRoutes.includes(name)) {
      this.stickyBoxData = {};
      // }
    },
    /**
     * Update the current url
     * @param {string} path - the current url
     */
    updatePath(path = this.$route.fullPath) {
      this.currentPath = path;
    },
    /**
     * Update the page title
     * @param {string} title
     */
    updateTitle(title) {
      if (this.currentTitle == undefined) {
        this.currentTitle = title;
      }
    },
    /**
     * Updates sticky box data
     * @param {event} - data-ready event
     *  {
     *    heading: 'Kandila',
     *    subheading: 'Unknown artist/maker',
     *    imageUuid: 'be019734-24ef-4625-94b6-7edfa98ef3b0',
     *     imageAlt: 'Object Overall'
     *  }
     */
    updateStickyBoxData(event) {
      this.stickyBoxData = event;
    }
  }
};
</script>

<style lang="scss">
@import "./layout-base.scss";
</style>
